import { useEffect, useRef } from 'react';

/**
 * Paints a colored outline ring around the element matching each peer's
 * current focus key. When the peer moves off, the outline is removed.
 *
 * Inline styles are used (with !important) so a parent fade or hover
 * style can't suppress the indicator.
 */
export default function RemoteHoverFocus({ focuses = {}, selfUserId }) {
  const decoratedRef = useRef(new Map()); // user_id -> { el, prev }

  useEffect(() => {
    Object.entries(focuses).forEach(([userId, f]) => {
      if (String(userId) === String(selfUserId)) return;

      // Tear down previous decoration for this user
      const prev = decoratedRef.current.get(userId);
      if (prev) {
        restore(prev);
        decoratedRef.current.delete(userId);
      }

      const key = f?.key;
      if (!key) return;

      let el = null;
      try {
        el = document.querySelector(
          `[data-live-focus-key="${cssEscape(key)}"]`
        );
      } catch {
        return;
      }
      if (!el) return;

      const isTeacher = f.role === 'teacher';
      const color = isTeacher
        ? 'rgba(59, 130, 246, 0.95)' // blue-500
        : 'rgba(16, 185, 129, 0.95)'; // emerald-500
      const glow = isTeacher
        ? 'rgba(59, 130, 246, 0.35)'
        : 'rgba(16, 185, 129, 0.35)';

      const previous = {
        el,
        boxShadow: el.style.boxShadow,
        outline: el.style.outline,
        outlineOffset: el.style.outlineOffset,
        priority: el.style.getPropertyPriority('box-shadow'),
      };
      el.style.setProperty(
        'box-shadow',
        `0 0 0 3px ${color}, 0 0 18px 4px ${glow}`,
        'important'
      );
      decoratedRef.current.set(userId, previous);
    });
  }, [focuses, selfUserId]);

  return null;
}

function restore({ el, boxShadow, outline, outlineOffset, priority }) {
  if (!el) return;
  if (boxShadow) {
    el.style.setProperty('box-shadow', boxShadow, priority || '');
  } else {
    el.style.removeProperty('box-shadow');
  }
  if (outline) el.style.outline = outline;
  if (outlineOffset) el.style.outlineOffset = outlineOffset;
}

function cssEscape(value) {
  if (typeof CSS !== 'undefined' && CSS.escape) return CSS.escape(value);
  // Fallback — restrictive but enough for our keys (alphanumeric + '-' + '_').
  return String(value).replace(/[^a-zA-Z0-9_-]/g, '\\$&');
}
